import React, { useState } from "react";
import { InfoBanner, TableHeader, WithRoleAccess } from "@services/ui-components";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import UpdateCompanyForm from "./UpdateCompanyForm";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useGetBranchesIsDeletedQuery } from "@me-team/host/main/company/graphql/company.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const UpdateCompany: React.FC = () => {
    const { t } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const navigate = useNavigate();
    const { branchId } = useParams();
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const canViewCompanyInfo = useRoleAccess({
        anyPermissions: [CompanyAccess.CompanyInfoView, CompanyAccess.CompanyInfoEdit],
    });
    const canEditCompanyInfo = useRoleAccess(CompanyAccess.CompanyInfoEdit);
    const canDeleteRestoreCompany = useRoleAccess(CompanyAccess.CompanyDeleteRestore);

    const { data: getBranchesIsDeleted } = useGetBranchesIsDeletedQuery({
        variables: {
            isDeleted: true,
            companyId: +currentCompanyId,
        },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const isDeletedBranches = (getBranchesIsDeleted?.user?.company?.branches?.length ?? 0) > 0;

    const handleDeleteCompany = () => {
        if (!canDeleteRestoreCompany) return;
        setShowModalDelete(true);
    };

    if (!canViewCompanyInfo) return null;

    return (
        <div className="mb-9 px-2 px-lg-0 pb-4">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t(
                        "On this page, you can enter information about your company." +
                            " Required fields are marked with an asterisk - *. If your company has several branches, you can create them using the button '+ Add branch'. If you want to restore the deleted branch - click 'Restore branch'"
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={t("Company")}
                colSize={6}
                btnColSize={6}
                buttons={
                    <Row className="d-flex justify-content-end">
                        <WithRoleAccess permission={CompanyAccess.BranchDeleteRestore}>
                            <Col xs={12} sm={6} xxl={5}>
                                <Button
                                    variant="outline-primary"
                                    className="w-100 mt-3 mt-lg-0  mb-3 mb-lg-0 fw-normal"
                                    onClick={() => navigate(`/branches/${branchId}`)}
                                    disabled={!isDeletedBranches}
                                >
                                    {t("Restore Branches")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <WithRoleAccess permission={CompanyAccess.CompanyDeleteRestore}>
                            <Col xs={12} sm={6} xxl={5}>
                                <Button
                                    variant="outline-orange"
                                    className="fw-normal w-100 mt-3 mt-lg-0  mb-3 mb-lg-0"
                                    onClick={handleDeleteCompany}
                                >
                                    {t("Delete Company")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                    </Row>
                }
            />
            <Row className="mt-4">
                <UpdateCompanyForm
                    setShowModalDelete={setShowModalDelete}
                    showModalDelete={showModalDelete}
                    canViewCompanyInfo={canViewCompanyInfo}
                    canEditCompanyInfo={canEditCompanyInfo}
                    canDeleteRestoreCompany={canDeleteRestoreCompany}
                />
            </Row>
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default UpdateCompany;
